import React, {useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import {FormattedMessage, useIntl} from 'react-intl';
import {Button, Col, Form, Row} from 'react-bootstrap';
import {Formik, FormikHelpers} from 'formik';
import {Editor} from '@tinymce/tinymce-react';
import {Person} from '../../types/types';
import {api} from '../../api';
import SingleFileUpload from '../../formik/SingleFileUpload';
import PageLoadingIndicator from '../Layout/PageLoadingIndicator';
import {emailValidation} from '../../util/validation';
import _ from "lodash";

import countriesDE from '../../i18n/countries/de.json';
import countriesEN from '../../i18n/countries/en.json';

export const URL_SLUG = 'profile';

const tinyMCEConfig = {
    'menubar': false,
    'statusbar': false,
    'resize': true,
    'paste_as_text': true,
    'forced_root_block': false,
    'fontsize_formats': '4pt 5pt 6pt 7pt 8pt 10pt 12pt 14pt 18pt 24pt 36pt',
    'toolbar': 'undo redo | bold italic underline | link unlink | removeformat',
    'plugins': 'colorpicker textcolor link paste',
    'entity_encoding': 'raw',
    'height': 300
};

const PersonalData: React.FC = () => {
    const [person, setPerson] = useState<Person | null>(null);
    const intl = useIntl();

    const countryListToOptions = (countries: any) => _.map(countries, (value, key) => {
        return {label: value, value: key};
    });

    const countryOptions = intl.locale === 'de' ? countryListToOptions(countriesDE.countries) : countryListToOptions(countriesEN.countries);

    useEffect(() => {
        api.fetchSelf()
            .then(res => {
                setPerson({...res});
            });
    }, []);

    const schema = () => {
        return yup.object({
            mainContact: yup.object().shape({
                company: yup.string().nullable(),
                function: yup.string().nullable(),
                salutation: yup.string().nullable(),
                title: yup.string().nullable(),
                firstName: yup.string().required(intl.formatMessage({id: 'person.form.field.required'})),
                lastName: yup.string().required(intl.formatMessage({id: 'person.form.field.required'})),
                street: yup.string().nullable(),
                houseno: yup.string().nullable(),
                postalCode: yup.string().nullable(),
                city: yup.string().nullable(),
                email: emailValidation(
                    intl.formatMessage({id: 'person.form.field.valid.email'})
                ).required(
                    intl.formatMessage({id: 'person.form.field.required'})
                ),
                phone: yup.string().nullable()
            }),
            pcoPersonData: yup.object().shape({
                vita: yup.string().nullable(),
                homepageUrl: yup.string().nullable(),
                image: yup.object().nullable(),
            })
        });
    };

    const handleSubmit = (values: Person, {setSubmitting}: FormikHelpers<any>): void => {
        api.updateSelf(values)
            .then(res => {
                setSubmitting(false);
                toast.success('Personendaten wurden erfolgreich bearbeitet', {
                    duration: 4000
                })
            })
            .catch(() => {
                toast.error('Personendaten konnten nicht bearbeitet werden', {
                    duration: 4000
                })
                setSubmitting(false);
            });
    };

    return (
        <>
            <h2>
                <FormattedMessage id="person.heading"/>
            </h2>

            {!person
                ? <div><PageLoadingIndicator/></div>
                : <Formik
                    enableReinitialize={true}
                    initialValues={person}
                    validationSchema={schema}
                    onSubmit={handleSubmit}
                >
                    {
                        ({
                             isSubmitting,
                             values,
                             handleSubmit,
                             handleChange,
                             errors,
                             setFieldValue
                         }) => (
                            <form onSubmit={handleSubmit}>
                                <Row>
                                    <Col sm={{span: 4, offset: 8}}>
                                        <Form.Group>
                                            <SingleFileUpload
                                                value={values.pcoPersonData.image}
                                                onChange={(value: {}) => setFieldValue('pcoPersonData.image', value)}
                                                accepts="image/bmp, image/png, image/jpeg image/jpg, image/gif"
                                                label="Foto / Logo"
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={{span: 4, offset: 8}}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.imageCaption'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="pcoPersonData.imageCaption"
                                                isInvalid={errors.pcoPersonData?.imageCaption !== undefined}
                                                onChange={handleChange}
                                                defaultValue={values.pcoPersonData.imageCaption}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                <div>{errors.pcoPersonData?.imageCaption}</div>
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm={{span: 4, offset: 8}}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.photographer'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="pcoPersonData.photographer"
                                                isInvalid={errors.pcoPersonData?.photographer !== undefined}
                                                onChange={handleChange}
                                                defaultValue={values.pcoPersonData.photographer}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                <div>{errors.pcoPersonData?.photographer}</div>
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.company'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.company"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.company}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.position'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.function"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.function}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.salutation'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.salutation"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.salutation}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.title'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.title"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.title}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.firstname'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.firstName"
                                                isInvalid={errors.mainContact?.firstName !== undefined}
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.firstName}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                <div>{errors.mainContact?.firstName}</div>
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.lastname'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.lastName"
                                                isInvalid={errors.mainContact?.lastName !== undefined}
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.lastName}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                <div>{errors.mainContact?.lastName}</div>
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.street'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.street"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.street}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.houseno'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.houseno"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.houseno}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.postalcode'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.postalCode"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.postalCode}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.city'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.city"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.city}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.country'}/>
                                            </Form.Label>
                                            <Form.Control
                                                as="select"
                                                defaultValue={values.mainContact.countryISOCode}
                                                name="mainContact.countryISOCode"
                                                onChange={handleChange}
                                            >
                                                <option value=""></option>

                                                {countryOptions.map(({label, value}, index) => (
                                                    <option key={index} value={value}>{label}</option>
                                                ))}
                                            </Form.Control>
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.phone'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.phone"
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.phone}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.email'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mainContact.email"
                                                isInvalid={errors.mainContact?.email !== undefined}
                                                onChange={handleChange}
                                                defaultValue={values.mainContact.email}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                <div>{errors.mainContact?.email}</div>
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                <FormattedMessage id={'person.form.label.homepage.url'}/>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="pcoPersonData.homepageUrl"
                                                isInvalid={errors.pcoPersonData?.homepageUrl !== undefined}
                                                onChange={handleChange}
                                                defaultValue={values.pcoPersonData.homepageUrl}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                <div>{errors.pcoPersonData?.homepageUrl}</div>
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                </Row>
                                <Row>
                                    <Col sm={12}>
                                        <Form.Group>
                                            <Form.Label>Vita</Form.Label>
                                            <Editor
                                                id="pcoPersonData.vita"
                                                onChange={(e: any) => setFieldValue('pcoPersonData.vita', e.target.getBody().innerHTML)}
                                                initialValue={values.pcoPersonData.vita}
                                                init={tinyMCEConfig}
                                            />
                                            <Form.Control.Feedback type="invalid"/>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Button variant="primary" type="submit" disabled={isSubmitting}>
                                    <FormattedMessage id={'person.form.button.save'}/>
                                </Button>
                            </form>
                        )}
                </Formik>
            }
        </>
    );
};

export default PersonalData;
